/**
 * 인사 활동 관리 서비스
 * 직원 히스토리 + 근무일정 히스토리 통합 관리
 */

import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  QueryDocumentSnapshot,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  where,
  doc,
  setDoc
} from 'firebase/firestore';
import { db } from './firebaseService';
import {
  UnifiedEmployeeHistoryData,
  EmployeeHistoryData,
  WorkScheduleHistoryData,
  HistoryType,
  EmployeeChangeType,
  ScheduleActionType,
  EmployeeHistoryFilterOptions,
  EmployeeHistoryStats
} from '@/types/employeeHistory';
import { Employee, WorkSchedule } from '@/types/employee';
import { checkWebAccess } from './subscriptionService';

/**
 * 인사 히스토리 서비스 클래스
 */
class EmployeeHistoryService {
  /**
   * 직원 히스토리 조회
   * @param shareBookId 공유가계부 ID
   * @param accountNumber 가계부 번호
   * @param limitCount 조회 개수
   * @param lastDoc 마지막 문서 (페이지네이션)
   */
  async getEmployeeHistory(
    shareBookId: string,
    accountNumber?: number,
    limitCount: number = 30,
    lastDoc?: QueryDocumentSnapshot
  ): Promise<{
    data: EmployeeHistoryData[];
    lastDoc: QueryDocumentSnapshot | null;
  }> {
    try {

      const collectionRef = collection(db, `sharedBooks/${shareBookId}/employee_history`);
      
      // 🔍 권한 문제 해결: where 절 없이 모든 데이터 가져온 후 클라이언트에서 필터링
      let q = query(
        collectionRef,
        orderBy('timestamp', 'desc'),
        limit(100) // 더 많은 데이터를 가져와서 클라이언트에서 필터링
      );

      // ❌ accountNumber where 절 제거 - Firestore 권한 문제 발생
      // if (accountNumber !== undefined) {
      //   q = query(q, where('accountNumber', '==', accountNumber), orderBy('timestamp', 'desc'), limit(limitCount));
      // }

      // 페이지네이션
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      
      let allData: EmployeeHistoryData[] = snapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          employeeId: docData.employeeId || '',
          employeeName: docData.employeeName || '',
          action: docData.action || '',
          changeType: docData.changeType || EmployeeChangeType.UPDATED,
          timestamp: docData.timestamp || Date.now(),
          description: docData.description || docData.details, // 앱은 details 필드 사용
          metadata: {
            accountNumber: docData.accountNumber, // 🔍 최상위 필드를 metadata로 이동
            ...docData.metadata
          }
        };
      });


      // 🔍 클라이언트에서 accountNumber 필터링
      if (accountNumber !== undefined) {
        allData = allData.filter(history => {
          // 앱은 최상위 필드에 accountNumber 저장, 웹은 metadata에 저장
          const historyAccountNumber = history.metadata?.accountNumber;
          return historyAccountNumber === accountNumber;
        });
      }

      // limit에 맞게 데이터 자르기
      const data = allData.slice(0, limitCount);


      return {
        data,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
      };
    } catch (error) {
      console.error('❌ 직원 히스토리 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 근무일정 히스토리 조회
   * @param shareBookId 공유가계부 ID
   * @param accountNumber 가계부 번호
   * @param limitCount 조회 개수
   * @param lastDoc 마지막 문서 (페이지네이션)
   */
  async getWorkScheduleHistory(
    shareBookId: string,
    accountNumber?: number,
    limitCount: number = 30,
    lastDoc?: QueryDocumentSnapshot
  ): Promise<{
    data: WorkScheduleHistoryData[];
    lastDoc: QueryDocumentSnapshot | null;
  }> {
    try {

      const collectionRef = collection(db, `sharedBooks/${shareBookId}/work_schedule_history`);
      
      // 🔍 권한 문제 해결: where 절 없이 모든 데이터 가져온 후 클라이언트에서 필터링
      let q = query(
        collectionRef,
        orderBy('timestamp', 'desc'),
        limit(100) // 더 많은 데이터를 가져와서 클라이언트에서 필터링
      );

      // ❌ accountNumber where 절 제거 - Firestore 권한 문제 발생
      // if (accountNumber !== undefined) {
      //   q = query(q, where('accountNumber', '==', accountNumber), orderBy('timestamp', 'desc'), limit(limitCount));
      // }

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      
      let allData: WorkScheduleHistoryData[] = snapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          scheduleId: docData.scheduleId || '',
          employeeId: docData.employeeId || '',
          employeeName: docData.employeeName || '',
          action: docData.action || '',
          actionType: docData.actionType || ScheduleActionType.UPDATED,
          workDate: docData.workDate || '',
          startTime: docData.startTime,
          endTime: docData.endTime,
          breakTime: docData.breakTime,
          timestamp: docData.timestamp || Date.now(),
          description: docData.description || docData.details, // 앱은 details 필드 사용
          metadata: {
            accountNumber: docData.accountNumber, // 🔍 최상위 필드를 metadata로 이동
            ...docData.metadata
          }
        };
      });


      // 🔍 클라이언트에서 accountNumber 필터링
      if (accountNumber !== undefined) {
        allData = allData.filter(history => {
          // 앱은 최상위 필드에 accountNumber 저장, 웹은 metadata에 저장
          const historyAccountNumber = history.metadata?.accountNumber;
          return historyAccountNumber === accountNumber;
        });
      }

      // limit에 맞게 데이터 자르기
      const data = allData.slice(0, limitCount);


      return {
        data,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
      };
    } catch (error) {
      console.error('❌ 근무일정 히스토리 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 통합 인사 히스토리 조회 (직원 + 근무일정)
   * @param shareBookId 공유가계부 ID
   * @param accountNumber 가계부 번호
   * @param options 조회 옵션
   */
  async getUnifiedEmployeeHistory(
    shareBookId: string,
    accountNumber?: number,
    options?: {
      limit?: number;
      lastEmployeeDoc?: QueryDocumentSnapshot;
      lastWorkScheduleDoc?: QueryDocumentSnapshot;
    }
  ): Promise<{
    data: UnifiedEmployeeHistoryData[];
    lastEmployeeDoc: QueryDocumentSnapshot | null;
    lastWorkScheduleDoc: QueryDocumentSnapshot | null;
  }> {
    try {

      const limitCount = options?.limit || 30;

      // 🔍 병렬 조회 전 파라미터 확인

      // 병렬로 두 히스토리 조회
      const [employeeResult, workScheduleResult] = await Promise.all([
        this.getEmployeeHistory(
          shareBookId,
          accountNumber,
          limitCount,
          options?.lastEmployeeDoc
        ),
        this.getWorkScheduleHistory(
          shareBookId,
          accountNumber,
          limitCount,
          options?.lastWorkScheduleDoc
        )
      ]);

      // 통합 및 정렬
      const unified = this.mergeAndSortHistories(
        employeeResult.data,
        workScheduleResult.data
      );


      return {
        data: unified,
        lastEmployeeDoc: employeeResult.lastDoc,
        lastWorkScheduleDoc: workScheduleResult.lastDoc
      };
    } catch (error) {
      console.error('❌ 통합 인사 히스토리 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 히스토리 통합 및 정렬 (시간순)
   */
  private mergeAndSortHistories(
    employeeHistories: EmployeeHistoryData[],
    workScheduleHistories: WorkScheduleHistoryData[]
  ): UnifiedEmployeeHistoryData[] {

    const unified: UnifiedEmployeeHistoryData[] = [];

    // 직원 히스토리 변환
    employeeHistories.forEach((history, index) => {
      const unifiedHistory = {
        id: history.id,
        type: HistoryType.EMPLOYEE,
        employeeName: history.employeeName,
        action: history.action,
        timestamp: history.timestamp,
        description: history.description,
        employeeId: history.employeeId,
        changeType: history.changeType,
        metadata: history.metadata
      };
      unified.push(unifiedHistory);
    });

    // 근무일정 히스토리 변환
    workScheduleHistories.forEach((history, index) => {
      const unifiedHistory = {
        id: history.id,
        type: HistoryType.WORK_SCHEDULE,
        employeeName: history.employeeName,
        action: history.action,
        timestamp: history.timestamp,
        description: history.description,
        scheduleId: history.scheduleId,
        workDate: history.workDate,
        startTime: history.startTime,
        endTime: history.endTime,
        breakTime: history.breakTime,
        metadata: history.metadata
      };
      unified.push(unifiedHistory);
    });


    // 시간순 정렬 (최신순)
    const sorted = unified.sort((a, b) => b.timestamp - a.timestamp);
    
    
    return sorted;
  }

  /**
   * 실시간 직원 히스토리 리스너 설정
   */
  onEmployeeHistorySnapshot(
    shareBookId: string,
    accountNumber: number | undefined,
    callback: (histories: EmployeeHistoryData[]) => void
  ): Unsubscribe {
    try {
      
      const collectionRef = collection(db, `sharedBooks/${shareBookId}/employee_history`);
      
      // 🔍 권한 문제 해결: 필터링 없이 모든 데이터 가져온 후 클라이언트에서 필터링
      let q = query(
        collectionRef,
        orderBy('timestamp', 'desc'),
        limit(100) // 더 많은 데이터를 가져와서 클라이언트에서 필터링
      );

      return onSnapshot(q, (snapshot) => {
        
        const histories: EmployeeHistoryData[] = snapshot.docs.map(doc => {
          const docData = doc.data();
          return {
            id: doc.id,
            employeeId: docData.employeeId || '',
            employeeName: docData.employeeName || '',
            action: docData.action || '',
            changeType: docData.changeType || EmployeeChangeType.UPDATED,
            timestamp: docData.timestamp || Date.now(),
            description: docData.description,
            metadata: docData.metadata
          };
        });

        // 🔍 클라이언트에서 accountNumber 필터링
        const filteredHistories = accountNumber !== undefined 
          ? histories.filter(history => {
              const historyAccountNumber = history.metadata?.accountNumber || history.metadata?.account_number;
              return historyAccountNumber === accountNumber;
            })
          : histories;

        callback(filteredHistories);
      }, (error) => {
        console.error('❌ 실시간 직원 히스토리 리스너 오류:', error);
        // 오류 발생 시 빈 배열로 콜백 호출하여 앱이 중단되지 않도록
        callback([]);
      });
    } catch (error) {
      console.error('❌ 직원 히스토리 리스너 설정 실패:', error);
      throw error;
    }
  }

  /**
   * 실시간 근무일정 히스토리 리스너 설정
   */
  onWorkScheduleHistorySnapshot(
    shareBookId: string,
    accountNumber: number | undefined,
    callback: (histories: WorkScheduleHistoryData[]) => void
  ): Unsubscribe {
    try {
      
      const collectionRef = collection(db, `sharedBooks/${shareBookId}/work_schedule_history`);
      
      // 🔍 권한 문제 해결: 필터링 없이 모든 데이터 가져온 후 클라이언트에서 필터링
      let q = query(
        collectionRef,
        orderBy('timestamp', 'desc'),
        limit(100) // 더 많은 데이터를 가져와서 클라이언트에서 필터링
      );

      return onSnapshot(q, (snapshot) => {
        
        const histories: WorkScheduleHistoryData[] = snapshot.docs.map(doc => {
          const docData = doc.data();
          return {
            id: doc.id,
            scheduleId: docData.scheduleId || '',
            employeeId: docData.employeeId || '',
            employeeName: docData.employeeName || '',
            action: docData.action || '',
            actionType: docData.actionType || ScheduleActionType.UPDATED,
            workDate: docData.workDate || '',
            startTime: docData.startTime,
            endTime: docData.endTime,
            breakTime: docData.breakTime,
            timestamp: docData.timestamp || Date.now(),
            description: docData.description,
            metadata: docData.metadata
          };
        });

        // 🔍 클라이언트에서 accountNumber 필터링
        const filteredHistories = accountNumber !== undefined 
          ? histories.filter(history => {
              const historyAccountNumber = history.metadata?.accountNumber || history.metadata?.account_number;
              return historyAccountNumber === accountNumber;
            })
          : histories;

        callback(filteredHistories);
      }, (error) => {
        console.error('❌ 실시간 근무일정 히스토리 리스너 오류:', error);
        // 오류 발생 시 빈 배열로 콜백 호출하여 앱이 중단되지 않도록
        callback([]);
      });
    } catch (error) {
      console.error('❌ 근무일정 히스토리 리스너 설정 실패:', error);
      throw error;
    }
  }

  /**
   * 인사 히스토리 통계 조회
   */
  async getEmployeeHistoryStats(
    shareBookId: string,
    accountNumber?: number
  ): Promise<EmployeeHistoryStats> {
    try {
      const result = await this.getUnifiedEmployeeHistory(
        shareBookId,
        accountNumber,
        { limit: 100 }
      );

      const employeeCounts: Record<string, number> = {};
      const actionTypeCounts: Record<string, number> = {};
      let employeeHistoryCount = 0;
      let workScheduleHistoryCount = 0;

      result.data.forEach(history => {
        // 직원별 카운트
        employeeCounts[history.employeeName] = (employeeCounts[history.employeeName] || 0) + 1;
        
        // 액션 타입별 카운트
        actionTypeCounts[history.action] = (actionTypeCounts[history.action] || 0) + 1;
        
        // 히스토리 타입별 카운트
        if (history.type === HistoryType.EMPLOYEE) {
          employeeHistoryCount++;
        } else {
          workScheduleHistoryCount++;
        }
      });

      return {
        totalCount: result.data.length,
        employeeHistoryCount,
        workScheduleHistoryCount,
        employeeCounts,
        actionTypeCounts,
        recentActivities: result.data.slice(0, 10)
      };
    } catch (error) {
      console.error('❌ 인사 히스토리 통계 조회 실패:', error);
      throw error;
    }
  }

  /**
   * Firestore 컬렉션 구조 디버깅
   * 실제 데이터 구조와 필드명 확인
   */
  async debugEmployeeHistoryStructure(shareBookId: string): Promise<void> {
    try {

      // 🔍 앱에서 사용하는 실제 shareBookId 후보들 확인

      // 1. 컬렉션 존재 여부 확인
      const employeeHistoryRef = collection(db, `sharedBooks/${shareBookId}/employee_history`);
      const scheduleHistoryRef = collection(db, `sharedBooks/${shareBookId}/work_schedule_history`);
      

      // 2. 직원 히스토리 샘플 조회 (정렬 없이)
      try {
        const employeeSnapshot = await getDocs(query(employeeHistoryRef, limit(3)));
        
      } catch (error) {
        console.error('❌ employee_history 조회 오류:', error);
      }

      // 3. 근무일정 히스토리 샘플 조회 (정렬 없이)
      try {
        const scheduleSnapshot = await getDocs(query(scheduleHistoryRef, limit(3)));
        
      } catch (error) {
        console.error('❌ work_schedule_history 조회 오류:', error);
      }

      // 4. 실제 서비스 메서드로 데이터 조회 테스트
      try {
        
        await this.getEmployeeHistory(shareBookId, undefined, 3);

        await this.getWorkScheduleHistory(shareBookId, undefined, 3);

      } catch (error) {
        console.error('❌ 서비스 메서드 테스트 실패:', error);
      }

    } catch (error) {
      console.error('❌ 인사 히스토리 구조 디버깅 실패:', error);
    }
  }

  /**
   * 직원 생성 히스토리 기록
   */
  async recordEmployeeCreated(
    userId: string,
    employee: Employee,
    { skipFirestoreSync = false } = {}
  ): Promise<void> {
    try {
      // 1. skipFirestoreSync가 true면 스킵 (Firestore 동기화에서 받은 데이터)
      if (skipFirestoreSync) {
        return;
      }

      // 2. 프리미엄/멤버 확인
      const hasWebAccess = await checkWebAccess(userId);
      if (!hasWebAccess) {
        return;
      }

      // 3. 중복 히스토리 기록 방지
      const cacheKey = `CREATE_${employee.id}_${employee.accountNumber}`;
      if (this.isHistoryAlreadyRecorded(cacheKey)) {
        return;
      }

      // 4. 히스토리 데이터 생성
      const historyId = crypto.randomUUID();
      const history = {
        id: historyId,
        employeeId: employee.id,
        employeeName: employee.name,
        action: 'CREATE',
        accountNumber: employee.accountNumber,
        timestamp: Date.now(),
        userId: userId,
        details: `직원이 생성되었습니다: ${employee.name}`,
        fieldName: null,
        oldValue: null,
        newValue: null
      };

      // 5. Firestore에 저장
      const historyRef = doc(db, `sharedBooks/${userId}/employee_history`, historyId);
      await setDoc(historyRef, history);

      // 6. 캐시 업데이트
      this.updateHistoryCache(cacheKey, history);

    } catch (error) {
      console.error('❌ 직원 생성 히스토리 기록 실패:', error);
      // 히스토리 기록 실패해도 메인 작업은 계속 진행
    }
  }

  /**
   * 직원 수정 히스토리 기록
   */
  async recordEmployeeUpdated(
    userId: string,
    oldEmployee: Employee,
    newEmployee: Employee,
    { skipFirestoreSync = false } = {}
  ): Promise<void> {
    try {
      // 1. skipFirestoreSync가 true면 스킵
      if (skipFirestoreSync) {
        return;
      }

      // 2. 프리미엄/멤버 확인
      const hasWebAccess = await checkWebAccess(userId);
      if (!hasWebAccess) {
        return;
      }

      // 3. 변경사항 감지
      const changes = this.getEmployeeChanges(oldEmployee, newEmployee);

      if (changes.length === 0) {
        return;
      }

      // 4. 각 변경사항별로 히스토리 기록
      for (const change of changes) {
        const cacheKey = `UPDATE_${newEmployee.id}_${change.field}_${newEmployee.accountNumber}`;

        if (this.isHistoryAlreadyRecorded(cacheKey)) {
          continue;
        }

        const historyId = crypto.randomUUID();
        const history = {
          id: historyId,
          employeeId: newEmployee.id,
          employeeName: newEmployee.name,
          action: 'UPDATE',
          fieldName: change.field,
          oldValue: change.oldValue?.toString() || '',
          newValue: change.newValue?.toString() || '',
          accountNumber: newEmployee.accountNumber,
          timestamp: Date.now(),
          userId: userId,
          details: `${change.field} 필드가 변경되었습니다: ${change.oldValue} → ${change.newValue}`
        };

        const historyRef = doc(db, `sharedBooks/${userId}/employee_history`, historyId);
        await setDoc(historyRef, history);

        this.updateHistoryCache(cacheKey, history);
      }

    } catch (error) {
      console.error('❌ 직원 수정 히스토리 기록 실패:', error);
      // 히스토리 기록 실패해도 메인 작업은 계속 진행
    }
  }

  /**
   * 직원 삭제 히스토리 기록
   */
  async recordEmployeeDeleted(
    userId: string,
    employee: Employee,
    { skipFirestoreSync = false } = {}
  ): Promise<void> {
    try {
      // 1. skipFirestoreSync가 true면 스킵
      if (skipFirestoreSync) {
        return;
      }

      // 2. 프리미엄/멤버 확인
      const hasWebAccess = await checkWebAccess(userId);
      if (!hasWebAccess) {
        return;
      }

      // 3. 중복 방지
      const cacheKey = `DELETE_${employee.id}_${employee.accountNumber}`;
      if (this.isHistoryAlreadyRecorded(cacheKey)) {
        return;
      }

      // 4. 히스토리 데이터 생성
      const historyId = crypto.randomUUID();
      const history = {
        id: historyId,
        employeeId: employee.id,
        employeeName: employee.name,
        action: 'DELETE',
        accountNumber: employee.accountNumber,
        timestamp: Date.now(),
        userId: userId,
        details: `직원이 삭제되었습니다: ${employee.name}`,
        fieldName: null,
        oldValue: null,
        newValue: null
      };

      // 5. Firestore에 저장
      const historyRef = doc(db, `sharedBooks/${userId}/employee_history`, historyId);
      await setDoc(historyRef, history);

      // 6. 캐시 업데이트
      this.updateHistoryCache(cacheKey, history);

    } catch (error) {
      console.error('❌ 직원 삭제 히스토리 기록 실패:', error);
      // 히스토리 기록 실패해도 메인 작업은 계속 진행
    }
  }

  /**
   * 변경사항 감지 메서드
   */
  private getEmployeeChanges(oldEmployee: Employee, newEmployee: Employee): Array<{
    field: string;
    oldValue: any;
    newValue: any;
  }> {
    const changes: Array<{ field: string; oldValue: any; newValue: any }> = [];

    // 각 필드 비교
    const fieldsToCompare: (keyof Employee)[] = [
      'name',
      'mp',
      'isHourly',
      'hourlyRate',
      'monthlySalary',
      'weeklyOffDays',
      'weeklyWorkHours',
      'taxRate',
      'dependents',
      'hasFourInsurance',
      'payday',
      'isweeklyWork',
      'hire_date',
      'severance_pay',
      'isRetired'
    ];

    for (const field of fieldsToCompare) {
      const oldValue = oldEmployee[field];
      const newValue = newEmployee[field];

      // 값이 다르면 변경사항으로 기록
      if (oldValue !== newValue) {
        changes.push({
          field: field as string,
          oldValue: oldValue ?? null,
          newValue: newValue ?? null
        });
      }
    }

    return changes;
  }

  /**
   * 중복 히스토리 기록 방지 캐시
   */
  private historyCache = new Map<string, { timestamp: number; data: any }>();
  private readonly CACHE_TTL = 5000; // 5초

  /**
   * 히스토리가 이미 기록되었는지 확인
   */
  private isHistoryAlreadyRecorded(cacheKey: string): boolean {
    const cached = this.historyCache.get(cacheKey);
    if (!cached) {
      return false;
    }

    // 캐시가 만료되었는지 확인
    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_TTL) {
      this.historyCache.delete(cacheKey);
      return false;
    }

    return true;
  }

  /**
   * 히스토리 캐시 업데이트
   */
  private updateHistoryCache(cacheKey: string, history: any): void {
    this.historyCache.set(cacheKey, {
      timestamp: Date.now(),
      data: history
    });

    // 캐시 크기 제한 (메모리 누수 방지)
    if (this.historyCache.size > 100) {
      const oldestKey = this.historyCache.keys().next().value;
      this.historyCache.delete(oldestKey);
    }
  }

  /**
   * 근무 일정 생성 히스토리 기록
   */
  async recordWorkScheduleCreated(
    userId: string,
    schedule: WorkSchedule,
    { skipFirestoreSync = false } = {}
  ): Promise<void> {
    try {
      // 1. skipFirestoreSync가 true면 스킵
      if (skipFirestoreSync) {
        return;
      }

      // 2. 프리미엄/멤버 확인
      const hasWebAccess = await checkWebAccess(userId);
      if (!hasWebAccess) {
        return;
      }

      // 3. 중복 히스토리 기록 방지
      const accountNumber = schedule.account_number || (schedule as any).accountNumber;
      const cacheKey = `SCHEDULE_CREATE_${schedule.id}_${accountNumber}`;
      if (this.isHistoryAlreadyRecorded(cacheKey)) {
        return;
      }

      // 4. 히스토리 데이터 생성
      const historyId = crypto.randomUUID();
      const employeeName = (schedule as any).employee_name || '';
      const history = {
        id: historyId,
        scheduleId: schedule.id,
        employeeId: schedule.employee_id,
        employeeName: employeeName,
        action: 'CREATE',
        actionType: ScheduleActionType.CREATED,
        workDate: schedule.date,
        startTime: schedule.start_time,
        endTime: schedule.end_time,
        breakTime: schedule.break_time,
        accountNumber: accountNumber,
        timestamp: Date.now(),
        userId: userId,
        description: `근무일정이 생성되었습니다: ${employeeName}`,
        details: `근무일정이 생성되었습니다: ${employeeName}`
      };

      // 5. Firestore에 저장
      const historyRef = doc(db, `sharedBooks/${userId}/work_schedule_history`, historyId);
      await setDoc(historyRef, history);

      // 6. 캐시 업데이트
      this.updateHistoryCache(cacheKey, history);

    } catch (error) {
      console.error('❌ 근무 일정 생성 히스토리 기록 실패:', error);
      // 히스토리 기록 실패해도 메인 작업은 계속 진행
    }
  }

  /**
   * 근무 일정 수정 히스토리 기록
   */
  async recordWorkScheduleUpdated(
    userId: string,
    oldSchedule: WorkSchedule,
    newSchedule: WorkSchedule,
    { skipFirestoreSync = false } = {}
  ): Promise<void> {
    try {
      // 1. skipFirestoreSync가 true면 스킵
      if (skipFirestoreSync) {
        return;
      }

      // 2. 프리미엄/멤버 확인
      const hasWebAccess = await checkWebAccess(userId);
      if (!hasWebAccess) {
        return;
      }

      // 3. 변경사항 감지
      const changes = this.getWorkScheduleChanges(oldSchedule, newSchedule);

      if (changes.length === 0) {
        return;
      }

      // 4. 각 변경사항별로 히스토리 기록
      const accountNumber = newSchedule.account_number || (newSchedule as any).accountNumber;
      for (const change of changes) {
        const cacheKey = `SCHEDULE_UPDATE_${newSchedule.id}_${change.field}_${accountNumber}`;

        if (this.isHistoryAlreadyRecorded(cacheKey)) {
          continue;
        }

        const historyId = crypto.randomUUID();
        const employeeName = (newSchedule as any).employee_name || '';
        const history = {
          id: historyId,
          scheduleId: newSchedule.id,
          employeeId: newSchedule.employee_id,
          employeeName: employeeName,
          action: 'UPDATE',
          actionType: ScheduleActionType.UPDATED,
          workDate: newSchedule.date,
          startTime: newSchedule.start_time,
          endTime: newSchedule.end_time,
          breakTime: newSchedule.break_time,
          accountNumber: accountNumber,
          timestamp: Date.now(),
          userId: userId,
          description: `근무일정이 수정되었습니다: ${employeeName}`,
          details: `근무일정이 수정되었습니다: ${employeeName}`
        };

        const historyRef = doc(db, `sharedBooks/${userId}/work_schedule_history`, historyId);
        await setDoc(historyRef, history);

        this.updateHistoryCache(cacheKey, history);
      }

    } catch (error) {
      console.error('❌ 근무 일정 수정 히스토리 기록 실패:', error);
      // 히스토리 기록 실패해도 메인 작업은 계속 진행
    }
  }

  /**
   * 근무 일정 삭제 히스토리 기록
   */
  async recordWorkScheduleDeleted(
    userId: string,
    schedule: WorkSchedule,
    { skipFirestoreSync = false } = {}
  ): Promise<void> {
    try {
      // 1. skipFirestoreSync가 true면 스킵
      if (skipFirestoreSync) {
        return;
      }

      // 2. 프리미엄/멤버 확인
      const hasWebAccess = await checkWebAccess(userId);
      if (!hasWebAccess) {
        return;
      }

      // 3. 중복 방지
      const accountNumber = schedule.account_number || (schedule as any).accountNumber;
      const cacheKey = `SCHEDULE_DELETE_${schedule.id}_${accountNumber}`;
      if (this.isHistoryAlreadyRecorded(cacheKey)) {
        return;
      }

      // 4. 히스토리 데이터 생성
      const historyId = crypto.randomUUID();
      const employeeName = (schedule as any).employee_name || '';
      const history = {
        id: historyId,
        scheduleId: schedule.id,
        employeeId: schedule.employee_id,
        employeeName: employeeName,
        action: 'DELETE',
        actionType: ScheduleActionType.DELETED,
        workDate: schedule.date,
        startTime: schedule.start_time,
        endTime: schedule.end_time,
        breakTime: schedule.break_time,
        accountNumber: accountNumber,
        timestamp: Date.now(),
        userId: userId,
        description: `근무일정이 삭제되었습니다: ${employeeName}`,
        details: `근무일정이 삭제되었습니다: ${employeeName}`
      };

      // 5. Firestore에 저장
      const historyRef = doc(db, `sharedBooks/${userId}/work_schedule_history`, historyId);
      await setDoc(historyRef, history);

      // 6. 캐시 업데이트
      this.updateHistoryCache(cacheKey, history);

    } catch (error) {
      console.error('❌ 근무 일정 삭제 히스토리 기록 실패:', error);
      // 히스토리 기록 실패해도 메인 작업은 계속 진행
    }
  }

  /**
   * 근무 일정 변경사항 감지 메서드
   */
  private getWorkScheduleChanges(oldSchedule: WorkSchedule, newSchedule: WorkSchedule): Array<{
    field: string;
    oldValue: any;
    newValue: any;
  }> {
    const changes: Array<{ field: string; oldValue: any; newValue: any }> = [];

    // 각 필드 비교
    const fieldsToCompare: (keyof WorkSchedule)[] = [
      'employee_id',
      'date',
      'start_time',
      'end_time',
      'break_time'
    ];

    for (const field of fieldsToCompare) {
      const oldValue = oldSchedule[field];
      const newValue = newSchedule[field];

      // 값이 다르면 변경사항으로 기록
      if (oldValue !== newValue) {
        changes.push({
          field: field as string,
          oldValue: oldValue ?? null,
          newValue: newValue ?? null
        });
      }
    }

    return changes;
  }
}

// 싱글톤 인스턴스
export const employeeHistoryService = new EmployeeHistoryService();
export default employeeHistoryService;

